import React, { useEffect, useState, useRef } from 'react';
import { Lock, AlertTriangle, Zap } from 'lucide-react';

/**
 * FocusLockOverlay — fullscreen "låst" mode during work focus.
 *
 * Limits:
 * - A web app CANNOT truly lock the phone OS. That requires a native app.
 * - What we DO: go fullscreen, keep screen awake (Wake Lock API),
 *   warn if user tries to leave (Page Visibility API),
 *   and show a visually clear lock screen so it's psychologically a commitment.
 */
export default function FocusLockOverlay({ timeLeft, total, onEmergencyExit, petEmoji = '🐾' }) {
  const [leftCount, setLeftCount] = useState(0);
  const wakeLockRef = useRef(null);
  const containerRef = useRef(null);

  // Enter fullscreen + wake lock on mount
  useEffect(() => {
    let cancelled = false;

    const goFull = async () => {
      try {
        const el = containerRef.current || document.documentElement;
        if (el.requestFullscreen) {
          await el.requestFullscreen();
        } else if (el.webkitRequestFullscreen) {
          await el.webkitRequestFullscreen();
        }
      } catch { /* user can decline — ok */ }
    };

    const wake = async () => {
      try {
        if ('wakeLock' in navigator) {
          const lock = await navigator.wakeLock.request('screen');
          if (!cancelled) wakeLockRef.current = lock;
        }
      } catch { /* ignore */ }
    };

    goFull();
    wake();

    // Visibility / blur detection
    const onVis = () => {
      if (document.hidden) {
        setLeftCount((n) => n + 1);
      }
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVis);
      try {
        if (document.fullscreenElement && document.exitFullscreen) document.exitFullscreen();
      } catch { /* ignore */ }
      if (wakeLockRef.current) {
        try { wakeLockRef.current.release(); } catch { /* ignore */ }
      }
    };
  }, []);

  // Re-acquire wake lock after returning visible
  useEffect(() => {
    const onVisible = async () => {
      if (!document.hidden && 'wakeLock' in navigator && !wakeLockRef.current) {
        try {
          wakeLockRef.current = await navigator.wakeLock.request('screen');
        } catch { /* ignore */ }
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const pct = total ? Math.max(0, Math.min(100, (1 - timeLeft / total) * 100)) : 0;
  const mm = String(Math.floor(timeLeft / 60)).padStart(2, '0');
  const ss = String(timeLeft % 60).padStart(2, '0');

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-[10000] flex flex-col items-center justify-center text-white overflow-hidden"
      style={{
        background: 'radial-gradient(circle at 50% 30%, #2D2060 0%, #120A2A 70%)',
      }}
      data-testid="focus-lock-overlay"
    >
      {/* Stars */}
      <div className="absolute inset-0 pointer-events-none opacity-40" aria-hidden>
        {[...Array(40)].map((_, i) => (
          <span
            key={i}
            className="absolute rounded-full bg-white"
            style={{
              top: `${(i * 37) % 100}%`,
              left: `${(i * 53) % 100}%`,
              width: `${((i % 3) + 1) * 1.5}px`,
              height: `${((i % 3) + 1) * 1.5}px`,
              opacity: ((i % 5) + 1) * 0.15,
              animation: `twinkle ${2 + (i % 4)}s ease-in-out ${i * 0.1}s infinite alternate`,
            }}
          />
        ))}
      </div>

      {/* Pet in center */}
      <div className="relative z-10 text-[150px] leading-none mb-6" style={{ animation: 'bobup 3s ease-in-out infinite' }}>
        {petEmoji}
      </div>

      {/* Timer */}
      <div className="relative z-10 flex items-center gap-2 mb-3">
        <Lock className="w-5 h-5" />
        <p className="text-lg font-bold tracking-wide font-['Fredoka']">FOKUSLÄGE</p>
      </div>
      <p
        className="text-[80px] sm:text-[110px] font-bold font-['Fredoka'] leading-none z-10"
        data-testid="focus-lock-timer"
      >
        {mm}:{ss}
      </p>
      <p className="text-sm opacity-70 mt-2 z-10">Din kompis väntar på dig. Håll ut 💛</p>

      {/* Progress */}
      <div className="w-full max-w-xs mt-6 z-10 px-4">
        <div className="h-2 bg-white/15 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#FFC8DD] via-[#C8B6FF] to-[#A2D2FF] transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {/* Left warning */}
      {leftCount > 0 && (
        <div
          className="mt-6 max-w-xs text-center bg-yellow-500/20 border-2 border-yellow-400/50 rounded-xl px-4 py-2 z-10"
          data-testid="focus-lock-warning"
        >
          <div className="flex items-center justify-center gap-1 text-yellow-300 text-xs font-bold mb-1">
            <AlertTriangle className="w-3 h-3" /> Du lämnade fliken {leftCount} gång{leftCount === 1 ? '' : 'er'}
          </div>
          <p className="text-xs opacity-90">Det är okej — kom tillbaka. Din kompis är stolt över att du försöker 💕</p>
        </div>
      )}

      {/* Emergency exit — requires long-press */}
      <EmergencyButton onExit={onEmergencyExit} />

      <style>{`
        @keyframes twinkle {
          from { opacity: 0.1; transform: scale(0.8); }
          to { opacity: 0.9; transform: scale(1); }
        }
        @keyframes bobup {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
      `}</style>
    </div>
  );
}

function EmergencyButton({ onExit }) {
  const [pressing, setPressing] = useState(false);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);

  const start = () => {
    setPressing(true);
    const startTime = Date.now();
    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const p = Math.min(100, (elapsed / 1500) * 100);
      setProgress(p);
      if (p >= 100) {
        clearInterval(timerRef.current);
        setPressing(false);
        setProgress(0);
        onExit();
      }
    }, 30);
  };
  const stop = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setPressing(false);
    setProgress(0);
  };

  return (
    <button
      onMouseDown={start}
      onMouseUp={stop}
      onMouseLeave={stop}
      onTouchStart={start}
      onTouchEnd={stop}
      className="mt-8 relative z-10 px-4 py-2 text-xs bg-white/10 hover:bg-white/20 border-2 border-white/30 rounded-full overflow-hidden"
      data-testid="focus-lock-emergency"
    >
      <span className="relative z-10 opacity-80">🐢 Tryck & håll 1.5s för att avbryta</span>
      {pressing && (
        <span
          className="absolute inset-0 bg-red-400/30"
          style={{ width: `${progress}%` }}
        />
      )}
    </button>
  );
}
